import React, { useState, useRef } from 'react';
import Button from '../../UI/Button';
import Loader from '../../UI/Loader';
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import { CSVColumn } from '../ImportWizard';

interface FileUploadStepProps {
    onNext: (file: File, data: any[], columns: CSVColumn[]) => void;
    title?: string;
    description?: React.ReactNode;
}

// File constraints
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_ROWS = 10000;

const FileUploadStep: React.FC<FileUploadStepProps> = ({ onNext, title = 'Upload Participant Data File', description }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file: File) => {
        // Validate file extension (.csv or .txt)
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith('.csv') && !fileName.endsWith('.txt')) {
            toast.error('Please upload a CSV (.csv) or tab-delimited text (.txt) file');
            return;
        }

        // Validate file size (max 50MB)
        if (file.size > MAX_FILE_SIZE_BYTES) {
            const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
            toast.error(`File size (${fileSizeMB} MB) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB`);
            return;
        }

        setFile(file);
    };

    const processCSV = () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }

        setLoading(true);

        // Determine delimiter based on file extension
        const isTabDelimited = file.name.toLowerCase().endsWith('.txt');
        const delimiter = isTabDelimited ? '\t' : ',';

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            delimiter: delimiter,
            transformHeader: (header: any) => header.trim(), // Remove whitespace from headers
            transform: (value: any) => value.trim(), // Remove whitespace from values
            complete: (results:any) => {
                // Filter out critical errors (ignore TooManyFields caused by trailing commas)
                const criticalErrors = results.errors.filter(
                    (error: any) => error.code !== 'TooManyFields' && error.type !== 'FieldMismatch'
                );

                if (criticalErrors.length > 0) {
                    console.error('CSV parsing errors:', criticalErrors);
                    toast.error('Error parsing file');
                    setLoading(false);
                    return;
                }

                // Remove rows where all values are empty
                /*const filteredData = results.data.filter((row: any) => {
                    return Object.values(row).some(
                        (value) => value !== null && value !== undefined && value !== ''
                    );
                });*/
                const filteredData = results.data.filter((row: any) => {
                    return Object.values(row).some(
                        (value) =>
                            value !== null &&
                            value !== undefined &&
                            String(value).trim() !== ''
                    );
                });

                if (filteredData.length === 0) {
                    toast.error('File is empty');
                    setLoading(false);
                    return;
                }

                // Enforce 10,000 row limit
                if (results.data.length > MAX_ROWS) {
                    toast.error(`File contains ${results.data.length.toLocaleString()} rows. Maximum allowed is ${MAX_ROWS.toLocaleString()} rows.`);
                    setLoading(false);
                    return;
                }

                // Enforce 10,000 row limit
                if (filteredData.length > MAX_ROWS) {
                    toast.error(
                        `File contains ${filteredData.length.toLocaleString()} rows. Maximum allowed is ${MAX_ROWS.toLocaleString()} rows.`
                    );
                    setLoading(false);
                    return;
                }

                // Extract columns with sample data
                const columns: CSVColumn[] = results.meta.fields?.map((field: any) => {
                    const sampleData = results.data
                        .slice(0, 3)
                        .map((row: any) => row[field] || '')
                        .filter(Boolean);
                    return {
                        name: field,
                        sampleData,
                    };
                }) || [];

                toast.success(
                    `Successfully loaded ${filteredData.length.toLocaleString()} rows`
                );

                onNext(file, filteredData, columns);

                setLoading(false);
            },
            error: (error: any) => {
                toast.error(`Error reading file: ${error.message}`);
                setLoading(false);
            },
        });
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    if (loading) {
        return (
            <div className="section">
                <Loader />
            </div>
        );
    }

    return (
        <div className="wizard-content">
            <div className="import-step">
                <h2 className="section-header">{title}</h2>
                <div className="margin-bottom-4">
                    {description || <>Select a CSV (.csv) or tab-delimited text (.txt) file to import participants.</>}
                    <br />
                    <strong>Maximum file size:</strong> {MAX_FILE_SIZE_MB}MB | <strong>Maximum rows:</strong> {MAX_ROWS.toLocaleString()}
                </div>
                <div
                    className={`file-upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'file-selected' : ''
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleFileInput}
                        style={{ display: 'none' }}
                    />

                    {!file ? (
                        <>
                            <div className="upload-icon">
                                <i className="ri-upload-cloud-line" style={{ fontSize: '48px', color: '#999' }} />
                            </div>
                            <p className="margin-vertical-3">
                                Drag & drop your CSV or tab-delimited file here, or click to browse.
                            </p>
                            <Button
                                id="browse-files"
                                text="Browse Files"
                                onClick={handleBrowseClick}
                            />
                        </>
                    ) : (
                        <>
                            <div className="file-info">
                                <i className="ri-file-text-line" style={{ fontSize: '48px', color: '#6b21a8' }} />
                                <p className="margin-top-2">
                                    <strong>{file.name}</strong>
                                </p>
                                <p className="text-muted">
                                    {(file.size / 1024).toFixed(2)} KB
                                </p>
                            </div>
                            <Button
                                id="change-file"
                                text="Change File"
                                className="secondary margin-top-3"
                                onClick={handleBrowseClick}
                            />
                        </>
                    )}
                </div>
            </div>
            <div className="row justify-content-end margin-top-5 step-button">
                <div className="column-auto">
                    <Button
                        id="next-button"
                        text="Next"
                        icon={<i className="ri-arrow-right-line" />}
                        iconLocation="end"
                        onClick={processCSV}
                        disabled={!file}
                    />
                </div>
            </div>
        </div>
    );
};

export default FileUploadStep;
